import {
  AppstoreOutlined,
  CheckCircleFilled,
  DesktopOutlined,
  EyeOutlined,
  FilterOutlined,
  SplitCellsOutlined,
  StopOutlined,
  TableOutlined,
  ThunderboltFilled,
  UnorderedListOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { MonitorColorBy, MonitorView } from "../../../../api/monitors";
import type { Station } from "../../../../api/stations";
import { getTranslation } from "../../../../utils/translations";

// Споделени части за формите на мониторите (създаване + редакция):
// баджът „Шпикер", изборът „Оцветяване" и видимостта на шпикер екрана.

// Кехлибарен бадж „Шпикер" — до името на потребителя в дропдауна/таблицата.
export const ShpickerTag = () => {
  const { t } = useTranslation();
  return (
    <span className="inline-flex shrink-0 items-center gap-[4px] rounded-full border border-brand-tint2 bg-brand-tint px-[8px] py-[2px] text-[10px] font-bold uppercase tracking-[0.6px] text-brand-deep-ink">
      <ThunderboltFilled className="text-[10px]" />
      {t("monitorsConfig.shpicker")}
    </span>
  );
};

// Ред за опция в user Select-а: име + бадж при шпикер. Ползва се през
// optionRender, за да остане option.label чист низ за търсачката.
export const UserOptionLabel = ({
  username,
  shpicker,
}: {
  username: string;
  shpicker: boolean;
}) => (
  <span className="flex items-center justify-between gap-[8px]">
    <span className="min-w-0 truncate">{username}</span>
    {shpicker && <ShpickerTag />}
  </span>
);

// Компактна опция-карта (в стила на ViewOption, но Tailwind-only).
const OptionCard = ({
  selected,
  onSelect,
  icon,
  title,
  desc,
}: {
  selected: boolean;
  onSelect: () => void;
  icon: React.ReactNode;
  title: string;
  desc?: string;
}) => (
  <button
    type="button"
    onClick={onSelect}
    className={`relative flex min-h-[52px] cursor-pointer items-center gap-[12px] rounded-[12px] border px-[14px] py-[8px] text-left transition-all duration-[120ms] ${
      selected
        ? "border-brand bg-brand-tint text-brand-deep-ink"
        : "border-shade-3 bg-surface text-ink hover:border-brand"
    }`}
  >
    {selected && (
      <CheckCircleFilled className="absolute right-[6px] top-[6px] text-[14px] text-brand-ink" />
    )}
    <span
      className={`flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[9px] border text-[17px] ${
        selected
          ? "border-brand-tint2 bg-surface text-brand-deep-ink"
          : "border-shade-2 bg-canvas text-body"
      }`}
    >
      {icon}
    </span>
    <span className="flex min-w-0 flex-col gap-[2px]">
      <span className="text-[13px] font-bold leading-[1.2] tracking-[-0.2px]">
        {title}
      </span>
      {desc && (
        <span
          className={`text-[11.5px] leading-[1.3] ${
            selected ? "text-brand-deep-ink opacity-85" : "text-body"
          }`}
        >
          {desc}
        </span>
      )}
    </span>
  </button>
);

// „Оцветяване" — по маси (както досега) / по сервитьор / без цвят.
export const ColorByPicker = ({
  value,
  onChange,
  isNarrow,
}: {
  value: MonitorColorBy;
  onChange: (value: MonitorColorBy) => void;
  isNarrow: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <div
      className={`grid gap-[10px] ${isNarrow ? "grid-cols-1" : "grid-cols-3"}`}
    >
      <OptionCard
        selected={value === "table"}
        onSelect={() => onChange("table")}
        icon={<TableOutlined />}
        title={t("monitorsConfig.colorByTable")}
        desc={t("monitorsConfig.colorByTableDesc")}
      />
      <OptionCard
        selected={value === "waiter"}
        onSelect={() => onChange("waiter")}
        icon={<UserOutlined />}
        title={t("monitorsConfig.colorByWaiter")}
        desc={t("monitorsConfig.colorByWaiterDesc")}
      />
      <OptionCard
        selected={value === "none"}
        onSelect={() => onChange("none")}
        icon={<StopOutlined />}
        title={t("monitorsConfig.colorByNone")}
        desc={t("monitorsConfig.colorByNoneDesc")}
      />
    </div>
  );
};

// „Изглед" — решетка или списък.
export const ViewPicker = ({
  value,
  onChange,
  isNarrow,
}: {
  value: MonitorView;
  onChange: (view: MonitorView) => void;
  isNarrow: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <div
      className={`grid gap-[10px] ${isNarrow ? "grid-cols-1" : "grid-cols-2"}`}
    >
      <OptionCard
        selected={value === "grid"}
        onSelect={() => onChange("grid")}
        icon={<AppstoreOutlined />}
        title={t("monitorsConfig.viewGrid")}
        desc={t("monitorsConfig.viewGridDesc")}
      />
      <OptionCard
        selected={value === "list"}
        onSelect={() => onChange("list")}
        icon={<UnorderedListOutlined />}
        title={t("monitorsConfig.viewList")}
        desc={t("monitorsConfig.viewListDesc")}
      />
    </div>
  );
};

// „Екран" — един екран (както досега) или разделен на две половини,
// всяка със свой списък станции.
export const ScreenModePicker = ({
  value,
  onChange,
  isNarrow,
}: {
  value: boolean;
  onChange: (splitScreen: boolean) => void;
  isNarrow: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <div
      className={`grid gap-[10px] ${isNarrow ? "grid-cols-1" : "grid-cols-2"}`}
    >
      <OptionCard
        selected={!value}
        onSelect={() => onChange(false)}
        icon={<DesktopOutlined />}
        title={t("monitorsConfig.screenSingle")}
        desc={t("monitorsConfig.screenSingleDesc")}
      />
      <OptionCard
        selected={value}
        onSelect={() => onChange(true)}
        icon={<SplitCellsOutlined />}
        title={t("monitorsConfig.screenSplit")}
        desc={t("monitorsConfig.screenSplitDesc")}
      />
    </div>
  );
};

// Решетка с бутони-станции — общата част на избора на станции в
// създаването и редакцията (и за целия екран, и за половина при split).
export const StationPickGrid = ({
  stations,
  selectedIds,
  onToggle,
  disabled = false,
  disabledIds,
  isNarrow,
}: {
  stations: Station[];
  selectedIds: number[];
  onToggle: (stationId: number) => void;
  disabled?: boolean;
  // Недостъпни поединично станции (при split: избраните в ДРУГАТА половина).
  disabledIds?: number[];
  isNarrow: boolean;
}) => {
  const { t, i18n } = useTranslation();
  if (stations.length === 0) {
    return (
      <div className="rounded-[10px] border border-shade-2 bg-surface p-[24px] text-center text-[13px] text-body">
        {t("monitorsConfig.noStations")}
      </div>
    );
  }
  return (
    <div
      className={`grid gap-[8px] ${
        isNarrow
          ? "grid-cols-[repeat(auto-fill,minmax(110px,1fr))]"
          : "grid-cols-[repeat(auto-fill,minmax(120px,1fr))]"
      }`}
    >
      {stations.map((station) => {
        const isSelected = selectedIds.includes(station.id);
        const isDisabled = disabled || (disabledIds?.includes(station.id) ?? false);
        const stationName = getTranslation(
          station.translations,
          i18n.language,
          station.slug,
        );
        return (
          <button
            key={station.id}
            type="button"
            disabled={isDisabled}
            onClick={() => onToggle(station.id)}
            className={`flex min-h-[42px] items-center gap-[8px] rounded-[10px] border px-[11px] py-[8px] transition-all duration-[120ms] ${
              isDisabled
                ? "cursor-not-allowed border-shade-2 bg-canvas text-muted opacity-55"
                : isSelected
                  ? "cursor-pointer border-brand bg-brand-tint text-brand-deep-ink"
                  : "cursor-pointer border-shade-3 bg-surface text-ink hover:border-brand"
            }`}
          >
            {station.color && (
              <span
                className="h-[9px] w-[9px] shrink-0 rounded-full"
                style={{
                  background: station.color,
                  boxShadow: "0 0 0 2px rgba(255,255,255,0.6) inset",
                }}
              />
            )}
            <span className="min-w-0 flex-1 truncate text-left text-[12.5px] font-semibold leading-[1.2] tracking-[-0.1px]">
              {stationName}
            </span>
            {isSelected && (
              <CheckCircleFilled className="shrink-0 text-[14px] text-brand-ink" />
            )}
          </button>
        );
      })}
    </div>
  );
};

// Изборът на станции при разделен екран: две озаглавени половини (лява/дясна),
// всяка със собствена решетка. Станция, избрана в едната половина, е
// недостъпна (сива) в другата — една станция живее само в една половина.
export const SplitStationsPicker = ({
  stations,
  leftIds,
  rightIds,
  onToggleLeft,
  onToggleRight,
  disabled = false,
  isNarrow,
}: {
  stations: Station[];
  leftIds: number[];
  rightIds: number[];
  onToggleLeft: (stationId: number) => void;
  onToggleRight: (stationId: number) => void;
  disabled?: boolean;
  isNarrow: boolean;
}) => {
  const { t } = useTranslation();
  const half = (
    label: string,
    ids: number[],
    onToggle: (stationId: number) => void,
    otherHalfIds: number[],
  ) => (
    <div className="flex flex-col gap-[8px]">
      <div className="flex items-center justify-between gap-[8px]">
        <span className="text-[10.5px] font-bold uppercase tracking-[1px] text-muted">
          {label}
        </span>
        {ids.length > 0 && (
          <span className="rounded-full border border-brand-tint2 bg-brand-tint px-[10px] py-[3px] text-[11px] font-bold tracking-[0.3px] text-brand-deep-ink">
            {t("monitorsConfig.selected", { count: ids.length })}
          </span>
        )}
      </div>
      <StationPickGrid
        stations={stations}
        selectedIds={ids}
        onToggle={onToggle}
        disabled={disabled}
        disabledIds={otherHalfIds}
        isNarrow={isNarrow}
      />
    </div>
  );
  return (
    <div
      className={`grid gap-[12px] ${isNarrow ? "grid-cols-1" : "grid-cols-2"}`}
    >
      {half(t("monitorsConfig.leftHalf"), leftIds, onToggleLeft, rightIds)}
      {half(t("monitorsConfig.rightHalf"), rightIds, onToggleRight, leftIds)}
    </div>
  );
};

// Видимост на шпикер екрана: само от „Сготвено" нататък, или всички статуси.
export const ShpickerVisibilityPicker = ({
  value,
  onChange,
  isNarrow,
}: {
  value: boolean;
  onChange: (showAllStatuses: boolean) => void;
  isNarrow: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <div
      className={`grid gap-[10px] ${isNarrow ? "grid-cols-1" : "grid-cols-2"}`}
    >
      <OptionCard
        selected={!value}
        onSelect={() => onChange(false)}
        icon={<FilterOutlined />}
        title={t("monitorsConfig.visibilityCookedOnly")}
        desc={t("monitorsConfig.visibilityCookedOnlyDesc")}
      />
      <OptionCard
        selected={value}
        onSelect={() => onChange(true)}
        icon={<EyeOutlined />}
        title={t("monitorsConfig.visibilityAll")}
        desc={t("monitorsConfig.visibilityAllDesc")}
      />
    </div>
  );
};
